import { text } from "../_constants/text";

type TranslationKey = string;
type TranslationParams = Record<string, string | number>;

/**
 * Get nested object value using dot notation
 * @param obj - The object to search in
 * @param path - The dot-separated path (e.g., 'common.loading')
 * @returns The value at the path or undefined if not found
 */
function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Replace placeholders in a string with provided parameters
 * @param text - The text containing placeholders like {name}
 * @param params - Object with key-value pairs to replace placeholders
 * @returns The text with placeholders replaced
 */
function replacePlaceholders(
  textValue: string,
  params: TranslationParams = {},
): string {
  return textValue.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
}

/**
 * Get translated text by key
 * @param key - The translation key using dot notation (e.g., 'common.loading')
 * @param params - Optional parameters to replace placeholders in the text
 * @param _locale - The locale to use (default: 'en') - currently unused but kept for API compatibility
 * @returns The translated text or the key if translation not found
 */
export function t(
  key: TranslationKey,
  params: TranslationParams = {},
  _locale: string = "en",
): string {
  const translation = getNestedValue(text, key);

  if (translation === undefined) {
    console.warn(`Translation missing for key: ${key}`);
    return key;
  }

  if (typeof translation !== "string") {
    console.warn(`Translation for key "${key}" is not a string:`, translation);
    return key;
  }

  return replacePlaceholders(translation, params);
}

/**
 * Check if a translation key exists
 * @param key - The translation key to check
 * @param _locale - The locale to check (default: 'en') - currently unused but kept for API compatibility
 * @returns True if the key exists, false otherwise
 */
export function hasTranslation(
  key: TranslationKey,
  _locale: string = "en",
): boolean {
  return getNestedValue(text, key) !== undefined;
}

/**
 * Get all translations for a locale (useful for debugging)
 * @param _locale - The locale to get translations for (default: 'en') - currently unused but kept for API compatibility
 * @returns The complete translations object
 */
export function getAllTranslations(_locale: string = "en") {
  return text;
}

/**
 * Initialize translations - now a no-op since we use constants
 * @param _locale - The locale to initialize (default: 'en') - currently unused but kept for API compatibility
 * @returns Promise that resolves immediately
 */
export async function initTranslations(_locale: string = "en"): Promise<void> {
  // No-op since we're using constants now
  return Promise.resolve();
}

/**
 * Clear the translations cache - now a no-op since we use constants
 */
export function clearTranslationsCache(): void {
  // No-op since we're using constants now
}

// Export the translation function as default for convenience
export default t;
